import {
    assertInArray,
    assertMaximum,
    assertMinimum,
    assertStringOrNumber,
} from '../../validators';
import type { CommonAttributeSettings, CommonMetadataKeys } from './common';
import { createAttributeSetter, DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata } from './common';

type StringOrNumberAttributeSettings = CommonAttributeSettings & {
    choices: string[];
    min: number;
    max: number;
};
export const INFERRED_NUMBER_ATTRIBUTE_TYPE = 'StringOrNumberAttributeType';
export type StringOrNumberMetadataKeys = keyof StringOrNumberAttributeSettings | CommonMetadataKeys;
const DEFAULT_SETTINGS: StringOrNumberAttributeSettings = {
    ...DEFAULT_ATTRIBUTE_SETTINGS,
    choices: [],
    min: -Infinity,
    max: Infinity,
};

/**
 * Creates an attribute decorator used to describe a string property. In addition to the shared
 * attribute settings, the string decorator allows validation to enforce the value is a member of inArray.
 */
export function StringOrNumberAttribute<T extends string | number>(
    settings: Partial<StringOrNumberAttributeSettings> = {},
) {
    settings = { ...DEFAULT_SETTINGS, ...settings };
    const validator = (value: number | string) => {
        assertStringOrNumber(value);

        if (typeof value === 'string') {
            if (settings.choices.length > 0) {
                assertInArray(value, settings.choices);
            }
        } else {
            assertMinimum(value, settings.min);
            assertMaximum(value, settings.max);
        }
    };
    return function <This>(
        { get, set }: ClassAccessorDecoratorTarget<This, T>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, T> {
        defineMetadata(settings, INFERRED_NUMBER_ATTRIBUTE_TYPE, context, get);
        return {
            get: function (this) {
                return get.call(this);
            },
            set: createAttributeSetter(validator, get, set),
        };
    };
}
